import React from 'react';
import { Dialog } from '../reusable';
import { Hash, User, Calendar, Package, Truck } from 'lucide-react';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL');
};

const Field = ({ label, value, isDarkMode }) => (
  <div style={{ marginBottom: 10 }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: isDarkMode ? '#a1a1aa' : '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 14, color: isDarkMode ? '#f4f4f5' : '#0f172a', marginTop: 2, wordBreak: 'break-word' }}>
      {value || '—'}
    </div>
  </div>
);

const PurchaseRequestDetailModal = ({
  isOpen,
  onClose,
  purchaseRequest,
  isDarkMode = false,
  onEdit,
}) => {
  if (!purchaseRequest) return null;
  const items = Array.isArray(purchaseRequest.items) ? purchaseRequest.items : [];

  const body = (
    <div style={{ padding: '0 20px 20px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 12, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
            Solicitud de compra
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: isDarkMode ? '#f4f4f5' : '#0f172a' }}>
            {purchaseRequest.request_code || '—'}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: isDarkMode ? '#d4d4d8' : '#475569' }}>
            {purchaseRequest.requester_name || 'Sin solicitante'}
            {purchaseRequest.status ? (
              <span
                style={{
                  marginLeft: 8,
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 11,
                  background: isDarkMode ? '#27272a' : '#f1f5f9',
                  color: isDarkMode ? '#e4e4e7' : '#334155',
                }}
              >
                {purchaseRequest.status}
              </span>
            ) : null}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: isDarkMode ? '#a1a1aa' : '#64748b', textTransform: 'uppercase' }}>
            Estimado
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: isDarkMode ? '#fafafa' : '#0f172a' }}>
            {formatMoney(purchaseRequest.estimated_total)}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px 24px' }}>
        <div>
          <h4 style={{ margin: '0 0 10px', fontSize: 13, color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
            <User size={16} /> Solicitante
          </h4>
          <Field label="Nombre" value={purchaseRequest.requester_name} isDarkMode={isDarkMode} />
          <Field label="Área" value={purchaseRequest.department} isDarkMode={isDarkMode} />
          <Field label="Prioridad" value={purchaseRequest.priority} isDarkMode={isDarkMode} />
        </div>
        <div>
          <h4 style={{ margin: '0 0 10px', fontSize: 13, color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Calendar size={16} /> Fechas
          </h4>
          <Field label="Fecha solicitud" value={formatDate(purchaseRequest.request_date)} isDarkMode={isDarkMode} />
          <Field label="Necesario para" value={formatDate(purchaseRequest.needed_by_date)} isDarkMode={isDarkMode} />
          <Field label="OC vinculada" value={purchaseRequest.purchase_order_code} isDarkMode={isDarkMode} />
        </div>
        <div>
          <h4 style={{ margin: '0 0 10px', fontSize: 13, color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Truck size={16} /> Proveedor sugerido
          </h4>
          <Field label="Nombre" value={purchaseRequest.supplier_name} isDarkMode={isDarkMode} />
          <Field label="Código" value={purchaseRequest.supplier_code} isDarkMode={isDarkMode} />
          <Field label="Justificación" value={purchaseRequest.justification} isDarkMode={isDarkMode} />
          <Field label="Notas" value={purchaseRequest.notes} isDarkMode={isDarkMode} />
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 13, color: isDarkMode ? '#e4e4e7' : '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Package size={16} /> Ítems ({items.length})
        </h4>
        {items.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>Sin ítems.</p>
        ) : (
          <div style={{ overflowX: 'auto', border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`, borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead>
                <tr style={{ background: isDarkMode ? '#27272a' : '#f8fafc' }}>
                  {['Código', 'Descripción', 'Cant.', 'Unidad', 'Precio est.', 'Total'].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '8px 10px',
                        textAlign: h === 'Cant.' || h === 'Precio est.' || h === 'Total' ? 'right' : 'left',
                        fontSize: 11,
                        color: isDarkMode ? '#a1a1aa' : '#64748b',
                        textTransform: 'uppercase',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id || `${item.item_code}-${item.description}`} style={{ borderTop: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}` }}>
                    <td style={{ padding: '8px 10px', fontSize: 12, color: isDarkMode ? '#d4d4d8' : '#475569' }}>{item.item_code || '—'}</td>
                    <td style={{ padding: '8px 10px', fontSize: 13, color: isDarkMode ? '#f4f4f5' : '#0f172a' }}>{item.description || '—'}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12, textAlign: 'right' }}>{item.quantity ?? 0}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12 }}>{item.unit || 'UN'}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12, textAlign: 'right' }}>{formatMoney(item.estimated_unit_price)}</td>
                    <td style={{ padding: '8px 10px', fontSize: 12, textAlign: 'right', fontWeight: 700 }}>{formatMoney(item.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {onEdit ? (
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => onEdit(purchaseRequest)}
            style={{
              height: 40,
              padding: '0 18px',
              borderRadius: 8,
              border: 'none',
              background: '#2563eb',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Editar solicitud
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Ficha de solicitud"
      subtitle={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Hash size={14} /> Resumen</span>}
      isDarkMode={isDarkMode}
      size="lg"
    >
      {body}
    </Dialog>
  );
};

export default PurchaseRequestDetailModal;
